"use client";

import React, { useState, useEffect } from "react";
import { KeenSliderOptions, useKeenSlider } from "keen-slider/react";
import Image from "next/image";
import { Arrow } from "../../Services/Review/Arrow"; // Your existing Arrow component
import SlideBottom from "./SlideBottom";
import { toWebp } from "@/utils/common";
import MobileSlide from "./mobileSlide";

interface SlideData {
  id: number;
  label: string;
  image: string;
  variant: "blue" | "green";
  title: string;
  details: string;
}

interface SliderProps {
  data?: any;
  isMobile?: boolean;
}

interface CustomKeenSliderOptions extends KeenSliderOptions {
  duration?: number;
}

const ThumbnailSlider: React.FC< SliderProps> = ({ data, isMobile  }) =>  {
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

  const sliderData: SlideData[] =
    data?.cards?.map(
      (card: any, index: number): SlideData => ({
        id: card.id || index + 1,
        label: `Slide ${index + 1}`,
        image: card.image
          ? `${baseUrl}${toWebp(card.image.data.attributes.url)}`
          : toWebp(`/images/ServicesInner/Slider-0${(index % 6) + 1}.jpg`),
        variant: index % 2 === 0 ? "blue" : "green",
        title: card.title || "",
        details: card.description || "",
      }),
    ) || [];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: true,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setLoaded(true);
    },
    duration: 4000,
    defaultAnimation: {
      duration: 1000, // transition duration in ms (1000 = 1s)
      easing: (t) => t, // optional: use linear or ease-out/in curve
    },
  } as CustomKeenSliderOptions);

  const [thumbRef, thumbInstanceRef] = useKeenSlider<HTMLDivElement>({
    loop: true,
    slides: {
      perView: 1,
      spacing: 0,
    },
    created() {
      setLoaded(true);
    },
    defaultAnimation: {
      duration: 1000, // transition duration in ms (1000 = 1s)
      easing: (t) => t, // optional: use linear or ease-out/in curve
    },
  });

  // Autoplay: Move main slider every 3 seconds
  useEffect(() => {
    if (!instanceRef.current || !thumbInstanceRef.current || isHovered) return;

    const interval = setInterval(() => {
      const nextIdx = (currentSlide + 1) % sliderData.length;

      instanceRef.current?.moveToIdx(nextIdx);
      thumbInstanceRef.current?.moveToIdx(nextIdx);
      setCurrentSlide(nextIdx); // update state manually
    }, 4000); // Match autoplay delay with your preference

    return () => clearInterval(interval);
  }, [
    currentSlide,
    isHovered,
    instanceRef,
    thumbInstanceRef,
    sliderData.length,
  ]);

  const goToSlide = (idx: number) => {
    instanceRef.current?.moveToIdx(idx);
    thumbInstanceRef.current?.moveToIdx(idx);
  };

  // Auto sync thumb slider with main slider
  useEffect(() => {
    thumbInstanceRef.current?.moveToIdx(currentSlide);
  }, [currentSlide, thumbInstanceRef]);

  useEffect(() => {
    const el = instanceRef.current?.container;
    if (
      el &&
      /Safari/i.test(navigator.userAgent) &&
      !/Chrome/i.test(navigator.userAgent)
    ) {
      el.style.webkitTransform = "translateZ(0)"; // force hardware acceleration
    }
  }, [instanceRef]);

  return (
    <div className="relative sub-services">
      {/* Main Slider */}
      <div className="lg:block hidden">
        <div
          className="keen-slider xl:max-w-[calc(100%-315px)] xl:w-[calc(100%-315px)] md:max-w-[calc(100%-220px)] max-w-[calc(100%-32px)] xl:aspect-[915/664] lg:aspect-[637/465] aspect-[349/253] w-full !overflow-hidden md:!absolute bottom-0 left-0 mx-auto h-full z-[1] "
          ref={sliderRef}
        >
          {sliderData.map((slide) => (
            <div
              key={slide.id}
              className="keen-slider__slide h-full flex items-center justify-center overflow-hidden"
            >
              <Image
                src={slide.image}
                width={915}
                height={664}
                alt={slide.label}
                className="w-full max-w-full h-full object-cover object-center"
                quality={100}
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                key={"main-slide" + slide.id}
              />
            </div>
          ))}
        </div>

        {/* Thumbnails / SlideBottom */}
        <div
          className="keen-slider w-full xl:max-w-[600px] md:max-w-[420px] z-[2] me-0 ms-auto xl:pt-[239px] lg:pt-[167px] md:pt-[80px]"
          ref={thumbRef}
        >
          {sliderData.map((slide, idx) => (
            <div
              key={idx}
              className="keen-slider__slide cursor-pointer"
              onClick={() => goToSlide(idx)}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <div className="flex justify-center border border-border xl:min-h-[425px] md:min-h-[297px] bg-background-dark h-full">
                <SlideBottom
                  title={slide.title}
                  details={slide.details}
                  variant={slide.variant}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Arrows */}
        {!isMobile && loaded && (
          <div className="md:block hidden absolute lg:bottom-8 bottom-5 lg:right-6 right-4 z-10">
            <div className="flex justify-center items-center gap-3 mt-10">
              <Arrow left onClick={() => instanceRef.current?.prev()} />
              <div className="pagination-indicator text-sm font-medium">
                <span className="text-subtitle-1 font-bold text-primary-text-white">
                  {currentSlide + 1} / {sliderData.length}
                </span>
              </div>
              <Arrow onClick={() => instanceRef.current?.next()} />
            </div>
          </div>
        )}
      </div>

      {/* Mobile dots */}
      {isMobile && (
        <>
          <MobileSlide slides={sliderData} />

          {/* <div className="md:hidden block">
            <div className="flex justify-center items-center gap-2 px-12 py-6 bg-transparent mx-auto max-w-[296px] mt-[32px]">
              {sliderData.map((_: SlideData, idx: number) => (
                <button
                  aria-label="dot"
                  key={idx}
                  onClick={() => goToSlide(idx)}
                  className={`dot${currentSlide === idx ? " active" : ""}`}
                >
                  <span className={currentSlide === idx ? "active" : ""}></span>
                </button>
              ))}
            </div>
          </div> */}
        </>
      )}
    </div>
  );
};
export default ThumbnailSlider;